// frontend/src/pages/superAdmin/ManageSchools.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../services/api';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';

const ManageSchools = ({ user, setUser }) => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSchool, setCurrentSchool] = useState({
    id: null,
    code: '',
    name: '',
    address: '',
    director: '',
    phone: '',
    email: ''
  });

  // Fetch schools data
  const fetchSchools = async () => {
    try {
      setLoading(true);
      const response = await api.get('/schools');
      setSchools(response.data);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลโรงเรียนได้',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSchool({
      ...currentSchool,
      [name]: value
    });
  };

  // Open add modal
  const handleAddClick = () => {
    setCurrentSchool({
      id: null,
      code: '',
      name: '',
      address: '',
      director: '',
      phone: '',
      email: ''
    });
    setEditMode(false);
    setShowModal(true);
  };

  // Open edit modal
  const handleEditClick = (school) => {
    setCurrentSchool(school);
    setEditMode(true);
    setShowModal(true);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editMode) {
        // Update school
        await api.put(`/schools/${currentSchool.id}`, {
          name: currentSchool.name,
          address: currentSchool.address,
          director: currentSchool.director,
          phone: currentSchool.phone,
          email: currentSchool.email
        });
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'อัปเดตข้อมูลโรงเรียนเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      } else {
        // Create school
        await api.post('/schools', currentSchool);
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'เพิ่มโรงเรียนเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      }
      
      // Refresh schools list
      fetchSchools();
      setShowModal(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถบันทึกข้อมูลได้',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  return (
    <div className="wrapper">
      <Header user={user} title="จัดการโรงเรียน" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">จัดการโรงเรียน</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item active">จัดการโรงเรียน</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            <div className="card card-navy">
              <div className="card-header">
                <h3 className="card-title">
                  รายการโรงเรียนในระบบ
                </h3>
                <div className="card-tools">
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handleAddClick}
                  >
                    <i className="fas fa-plus"></i> เพิ่มโรงเรียน
                  </button>
                </div>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">กำลังโหลด...</span>
                    </div>
                    <p className="mt-2">กำลังโหลดข้อมูล...</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr className="bg-navy">
                          <th style={{ width: '5%' }}>ลำดับ</th>
                          <th style={{ width: '10%' }}>รหัสโรงเรียน</th>
                          <th style={{ width: '20%' }}>ชื่อโรงเรียน</th>
                          <th style={{ width: '20%' }}>ผู้อำนวยการ</th>
                          <th style={{ width: '15%' }}>เบอร์โทรศัพท์</th>
                          <th style={{ width: '15%' }}>อีเมล</th>
                          <th style={{ width: '15%' }}>จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schools.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center">
                              ไม่พบข้อมูลโรงเรียน
                            </td>
                          </tr>
                        ) : (
                          schools.map((school, index) => (
                            <tr key={school.id}>
                              <td className="text-center">{index + 1}</td>
                              <td>{school.code}</td>
                              <td>{school.name}</td>
                              <td>{school.director || '-'}</td>
                              <td>{school.phone || '-'}</td>
                              <td>{school.email || '-'}</td>
                              <td className="text-center">
                                <button
                                  className="btn btn-info btn-sm me-2"
                                  onClick={() => handleEditClick(school)}
                                >
                                  <i className="fas fa-edit"></i> แก้ไข
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      {/* Modal for Add/Edit School */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-navy">
                <h5 className="modal-title">
                  {editMode ? 'แก้ไขข้อมูลโรงเรียน' : 'เพิ่มโรงเรียน'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="code" className="form-label">
                        รหัสโรงเรียน <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="code"
                        name="code"
                        value={currentSchool.code}
                        onChange={handleInputChange}
                        required
                        readOnly={editMode}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="name" className="form-label">
                        ชื่อโรงเรียน <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        value={currentSchool.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label htmlFor="address" className="form-label">
                        ที่อยู่
                      </label>
                      <textarea
                        className="form-control"
                        id="address"
                        name="address"
                        rows="3"
                        value={currentSchool.address || ''}
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="director" className="form-label">
                        ผู้อำนวยการ
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="director"
                        name="director"
                        value={currentSchool.director || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="phone" className="form-label">
                        เบอร์โทรศัพท์
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="phone"
                        name="phone"
                        value={currentSchool.phone || ''}
                        onChange={handleInputChange}
                        maxLength="10"
                        pattern="[0-9]{10}"
                        title="กรุณากรอกเบอร์โทรศัพท์ 10 หลัก"
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="email" className="form-label">
                        อีเมล
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={currentSchool.email || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    ยกเลิก
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editMode ? 'บันทึกการแก้ไข' : 'เพิ่มโรงเรียน'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSchools;